import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, ToggleLeft, ToggleRight, Save, X, AlertTriangle, ChefHat, TrendingUp, Search, ArrowUpDown, Smartphone, CircleDollarSign, LayoutGrid, AlertCircle } from 'lucide-react';
import { pricingAPI } from '../../utils/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTheme } from '../../contexts/ThemeContext';
import Modal from '../Layout/Modal.jsx';

const ApplicationsPricing = () => {
  const { t, i18n } = useTranslation();
  const { formatCurrency } = useCurrency();
  const { theme, isDark } = useTheme();
  
  const [applications, setApplications] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('applications'); // 'applications' or application ID
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('id');
  
  // Applications table search and sort
  const [applicationsSearchTerm, setApplicationsSearchTerm] = useState('');
  const [applicationsSortOrder, setApplicationsSortOrder] = useState('id');
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    application_name: '',
    percentage: '',
    is_active: true
  });
  
  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [applicationsData, pricingData] = await Promise.all([
        pricingAPI.getApplications(),
        pricingAPI.getAllPricing()
      ]);
      setApplications(applicationsData);
      setDishes(pricingData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(t('applicationsPricing.failedToLoadData'));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const resetForm = () => {
    setFormData({
      application_name: '',
      percentage: '',
      is_active: true
    });
    setError(null);
  };
  
  const handleAddApplication = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      // Validate form
      if (!formData.application_name.trim()) {
        setError(t('applicationsPricing.applicationNameRequired'));
        return;
      }
      
      if (!formData.percentage || formData.percentage < 0 || formData.percentage > 1000) {
        setError(t('applicationsPricing.percentageMustBeBetween'));
        return;
      }
      
      const newApplication = await pricingAPI.createApplication({
        application_name: formData.application_name.trim(),
        percentage: parseFloat(formData.percentage),
        is_active: formData.is_active
      });
      
      setApplications(prev => [...prev, newApplication]);
      resetForm();
      setShowAddForm(false);
    } catch (err) {
      console.error('Error creating application:', err);
      setError(err.message || t('applicationsPricing.failedToCreatePricingApplication'));
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleUpdateApplication = async (id) => {
    try {
      setIsSaving(true);
      setError(null);
      
      const application = applications.find(app => app.id === id);
      if (!application) return;
      
      // Validate form
      if (!application.application_name.trim()) {
        setError(t('applicationsPricing.applicationNameRequired'));
        return;
      }
      
      if (!application.percentage || application.percentage < 0 || application.percentage > 1000) {
        setError(t('applicationsPricing.percentageMustBeBetween'));
        return;
      }
      
      const updatedApplication = await pricingAPI.updateApplication(id, {
        application_name: application.application_name.trim(),
        percentage: parseFloat(application.percentage),
        is_active: application.is_active
      });
      
      setApplications(prev => 
        prev.map(app => app.id === id ? updatedApplication : app)
      );
      setEditingId(null);
    } catch (err) {
      console.error('Error updating application:', err);
      setError(err.message || t('applicationsPricing.failedToUpdatePricingApplication'));
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeleteApplication = (id) => {
    const application = applications.find(app => app.id === id);
    setApplicationToDelete(application);
    setShowDeleteModal(true);
  };
  
  const confirmDeleteApplication = async () => {
    if (!applicationToDelete) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      await pricingAPI.deleteApplication(applicationToDelete.id);
      setApplications(prev => prev.filter(app => app.id !== applicationToDelete.id));
      
      // If we're viewing this application's tab, switch back to applications list
      if (activeTab === applicationToDelete.id.toString()) {
        setActiveTab('applications');
      }
      
      setShowDeleteModal(false);
      setApplicationToDelete(null);
    } catch (err) {
      console.error('Error deleting application:', err);
      setError(err.message || t('applicationsPricing.failedToDeletePricingApplication'));
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleToggleActive = async (id) => {
    try {
      setIsSaving(true);
      setError(null);
      
      const updatedApplication = await pricingAPI.toggleApplication(id);
      setApplications(prev => 
        prev.map(app => app.id === id ? updatedApplication : app)
      );
    } catch (err) {
      console.error('Error toggling application status:', err);
      setError(err.message || t('applicationsPricing.failedToToggleApplicationStatus'));
    } finally {
      setIsSaving(false);
    }
  };
  
  const startEdit = (id) => {
    setEditingId(id);
    setShowAddForm(false);
  };
  
  const cancelEdit = () => {
    setEditingId(null);
    loadData(); // Reload to reset any changes
  };
  
  const cancelAdd = () => {
    setShowAddForm(false);
    resetForm();
  };
  
  const calculateNewPrice = (currentPrice, percentage) => {
    if (!currentPrice || !percentage) return 0;
    // New formula: New Price = Current Price / (1 - (Markup / 100))
    return currentPrice / (1 - (percentage / 100));
  };
  
  const getDishesWithPricing = () => {
    return dishes.filter(dish => dish.selling_price && dish.selling_price > 0);
  };
  
  const getFilteredAndSortedDishes = () => {
    let filteredDishes = getDishesWithPricing();
    
    // Apply search filter
    if (searchTerm) {
      filteredDishes = filteredDishes.filter(dish => {
        const dishName = (dish.recipe_name_en || dish.recipe_name_ar || '').toLowerCase();
        const searchLower = searchTerm.toLowerCase();
        return dishName.includes(searchLower) || dish.recipe_id.toString().includes(searchLower);
      });
    }
    
    // Apply sorting
    filteredDishes.sort((a, b) => {
      switch (sortOrder) {
        case 'id':
          return a.recipe_id - b.recipe_id;
        case 'name':
          const nameA = (a.recipe_name_en || a.recipe_name_ar || '').toLowerCase();
          const nameB = (b.recipe_name_en || b.recipe_name_ar || '').toLowerCase();
          return nameA.localeCompare(nameB);
        case 'price':
          return parseFloat(a.selling_price) - parseFloat(b.selling_price);
        case 'price-desc':
          return parseFloat(b.selling_price) - parseFloat(a.selling_price);
        default:
          return a.recipe_id - b.recipe_id;
      }
    });
    
    return filteredDishes;
  };
  
  const getFilteredAndSortedApplications = () => {
    let filteredApplications = applications.filter(app => {
      const searchLower = applicationsSearchTerm.toLowerCase();
      return (
        app.application_name.toLowerCase().includes(searchLower) ||
        app.id.toString().includes(searchLower)
      );
    });
    
    filteredApplications.sort((a, b) => {
      switch (applicationsSortOrder) {
        case 'name':
          return a.application_name.localeCompare(b.application_name);
        default:
          return a.id - b.id;
      }
    });
    
    return filteredApplications;
  };
  
  const getActiveApplication = () => {
    return applications.find(app => app.id.toString() === activeTab);
  };
  
  // Check if current active tab is still valid (active application)
  useEffect(() => {
    if (activeTab !== 'applications') {
      const activeApp = applications.find(app => app.id.toString() === activeTab);
      if (!activeApp || !activeApp.is_active) {
        setActiveTab('applications');
      }
    }
  }, [applications, activeTab]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('applicationsPricing.loadingPricingApplications')}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6 bg-background text-foreground min-h-screen p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('applicationsPricing.title')}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {t('applicationsPricing.subtitle')}
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-end mt-4 sm:mt-0">
          <button
            onClick={() => {
              setShowAddForm(true);
              setEditingId(null);
              resetForm();
            }}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-foreground bg-primary border-2 border-primary rounded-md hover:scale-105 transition-transform duration-200 focus:outline-none focus:ring-2 focus:ring-ring"
            disabled={isSaving}
            title="Add Application"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('applicationsPricing.addApplication')}
          </button>
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-destructive mr-2" />
            <p className="text-destructive">{error}</p>
          </div>
        </div>
      )}
      
      {/* Add Form */}
      {showAddForm && (
        <div className="mb-6 bg-card border border-border rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-lg font-semibold text-card-foreground">{t('applicationsPricing.addNewPricingApplication')}</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  {t('applicationsPricing.applicationName')}
                </label>
                <input
                  type="text"
                  value={formData.application_name}
                  onChange={(e) => handleInputChange('application_name', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground"
                  placeholder={t('applicationsPricing.applicationNamePlaceholder')}
                  disabled={isSaving}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-card-foreground mb-2">
                  {t('applicationsPricing.percentageMarkup')}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.percentage}
                    onChange={(e) => handleInputChange('percentage', e.target.value)}
                    className="w-full py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground"
                    style={{
                      paddingLeft: i18n.language === 'ar' ? '2.5rem' : '0.75rem',
                      paddingRight: i18n.language === 'ar' ? '0.75rem' : '2.5rem'
                    }}
                    placeholder="0.00"
                    min="0"
                    max="1000"
                    step="0.01"
                    disabled={isSaving}
                  />
                  <span className={`absolute top-1/2 transform -translate-y-1/2 text-muted-foreground text-sm ${
                    i18n.language === 'ar' ? 'left-3' : 'right-3'
                  }`}>
                    %
                  </span>
                </div>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={handleAddApplication}
                  disabled={isSaving}
                  className={`inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 ${
                    i18n.language === 'ar' ? 'ml-4' : 'mr-2'
                  }`}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? t('applicationsPricing.saving') : t('applicationsPricing.save')}
                </button>
                <button
                  onClick={cancelAdd}
                  disabled={isSaving}
                  className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('applicationsPricing.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Tabs */}
      <div className="mb-6">
        <div className="flex bg-muted rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab('applications')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === 'applications'
                ? 'bg-card text-primary shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Smartphone className="h-4 w-4" />
            {t('applicationsPricing.applicationsList')}
          </button>
          
          {applications.filter(app => app.is_active).map((application) => (
            <button
              key={application.id}
              onClick={() => setActiveTab(application.id.toString())}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === application.id.toString()
                  ? 'bg-card text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
              {application.application_name}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'applications' ? (
        <div className="space-y-6">
          {/* Search and Sort Controls */}
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder={t('applicationsPricing.searchByApplicationNameOrId')}
                  value={applicationsSearchTerm}
                  onChange={(e) => setApplicationsSearchTerm(e.target.value)}
                  className="w-full pl-10 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground"
                  style={{ 
                    paddingLeft: '2.75rem',
                    paddingRight: i18n.language === 'ar' ? '1.25rem' : '1rem'
                  }}
                />
                {applicationsSearchTerm && (
                  <button
                    onClick={() => setApplicationsSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-card-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Sort Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  const sortOptions = ['id', 'name'];
                  const currentIndex = sortOptions.indexOf(applicationsSortOrder);
                  const nextIndex = (currentIndex + 1) % sortOptions.length;
                  setApplicationsSortOrder(sortOptions[nextIndex]);
                }}
                className="inline-flex items-center justify-center h-10 px-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                title={applicationsSortOrder === 'id' ? t('applicationsPricing.sortById') : t('applicationsPricing.sortByName')}
              >
                <ArrowUpDown className="h-4 w-4" />
              </button>
          </div>

          {/* Applications Table */}
          <div className="bg-card border border-border rounded-lg shadow-sm">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-lg font-semibold text-card-foreground">{t('applicationsPricing.pricingApplications')}</h3>
            </div>
            <div className="p-6">
            {getFilteredAndSortedApplications().length === 0 ? (
              <div className="text-center py-12">
                <div className="text-muted-foreground mb-4">
                  <Smartphone className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-card-foreground mb-2">
                  {applicationsSearchTerm ? t('applicationsPricing.noApplicationsFound') : t('applicationsPricing.noPricingApplicationsFound')}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {applicationsSearchTerm 
                    ? t('applicationsPricing.tryAdjustingSearchCriteria')
                    : t('applicationsPricing.createFirstPricingApplication')
                  }
                </p>
                {!applicationsSearchTerm && (
                  <button
                    onClick={() => {
                      setShowAddForm(true);
                      setEditingId(null);
                      resetForm();
                    }}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    title="Add Application"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('applicationsPricing.addApplication')}
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getFilteredAndSortedApplications().map((application) => (
                  <div key={application.id} className="bg-background border border-border rounded-lg p-6 hover:border-primary/30 transition-colors">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <LayoutGrid className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">#{application.id}</div>
                          {editingId === application.id ? (
                            <input
                              type="text"
                              value={application.application_name}
                              onChange={(e) => {
                                setApplications(prev => 
                                  prev.map(app => 
                                    app.id === application.id 
                                      ? { ...app, application_name: e.target.value }
                                      : app
                                  )
                                );
                              }}
                              className="w-full px-3 py-1 border border-border rounded-md focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground text-sm font-medium"
                              disabled={isSaving}
                            />
                          ) : (
                            <h3 className="text-sm font-medium text-card-foreground">
                              {application.application_name}
                            </h3>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleActive(application.id)}
                        disabled={isSaving}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                          application.is_active
                            ? 'bg-chart-3/10 text-chart-3'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {application.is_active ? (
                          <ToggleRight className="h-3 w-3 mr-1" />
                        ) : (
                          <ToggleLeft className="h-3 w-3 mr-1" />
                        )}
                        {application.is_active ? t('applicationsPricing.active') : t('applicationsPricing.inactive')}
                      </button>
                    </div>

                    {/* Content */}
                    <div className="space-y-3 mb-4">
                      <div>
                        <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('applicationsPricing.percentage')}</label>
                        {editingId === application.id ? (
                          <div className="relative mt-1">
                            <input
                              type="number"
                              value={application.percentage}
                              onChange={(e) => {
                                setApplications(prev => 
                                  prev.map(app => 
                                    app.id === application.id 
                                      ? { ...app, percentage: e.target.value }
                                      : app
                                  )
                                );
                              }}
                              className="w-full py-2 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground"
                              style={{
                                paddingLeft: i18n.language === 'ar' ? '2.5rem' : '0.75rem',
                                paddingRight: i18n.language === 'ar' ? '0.75rem' : '2.5rem'
                              }}
                              min="0"
                              max="1000"
                              step="0.01"
                              disabled={isSaving}
                            />
                            <span className={`absolute top-1/2 transform -translate-y-1/2 text-muted-foreground text-sm ${
                              i18n.language === 'ar' ? 'left-3' : 'right-3'
                            }`}>
                              %
                            </span>
                          </div>
                        ) : (
                          <div className="text-lg font-semibold text-card-foreground mt-1">
                            {application.percentage}%
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <label className="text-xs text-muted-foreground uppercase tracking-wider">{t('applicationsPricing.created')}</label>
                        <div className="text-sm text-muted-foreground mt-1">
                          {new Date(application.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t border-border">
                      {editingId === application.id ? (
                        <>
                          <button
                            onClick={() => handleUpdateApplication(application.id)}
                            disabled={isSaving}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
                          >
                            <Save className="h-4 w-4 mr-1" />
                            {t('applicationsPricing.save')}
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={isSaving}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
                          >
                            <X className="h-4 w-4 mr-1" />
                            {t('applicationsPricing.cancel')}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(application.id)}
                            disabled={isSaving}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            {t('applicationsPricing.edit')}
                          </button>
                          <button
                            onClick={() => handleDeleteApplication(application.id)}
                            disabled={isSaving}
                            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-destructive rounded-lg text-sm font-medium text-destructive bg-background hover:bg-destructive hover:text-destructive-foreground focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive transition-all duration-200 disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            {t('applicationsPricing.delete')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>
      ) : (
        <div>
          {/* Search and Sort Controls */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder={t('applicationsPricing.searchByDishNameOrId')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground"
                style={{ 
                  paddingLeft: '2.75rem',
                  paddingRight: i18n.language === 'ar' ? '1.25rem' : '1rem'
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-card-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Sort Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              const sortOptions = ['id', 'name', 'price', 'price-desc'];
              const currentIndex = sortOptions.indexOf(sortOrder);
              const nextIndex = (currentIndex + 1) % sortOptions.length;
              setSortOrder(sortOptions[nextIndex]);
            }}
            className="inline-flex items-center justify-center h-10 px-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            title={
              sortOrder === 'id' ? t('applicationsPricing.sortById') :
              sortOrder === 'name' ? t('applicationsPricing.sortByName') :
              sortOrder === 'price' ? t('applicationsPricing.sortByPrice') :
              t('applicationsPricing.sortByPriceDesc')
            }
          >
            <ArrowUpDown className="h-4 w-4" />
          </button>
        </div>

        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-lg font-semibold text-card-foreground">
              {getActiveApplication()?.application_name} - {t('applicationsPricing.dishPricing')}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t('applicationsPricing.showingDishesWithPricing', { percentage: getActiveApplication()?.percentage })}
            </p>
          </div>
          
          <div className="p-0">
            {getFilteredAndSortedDishes().length === 0 ? (
              <div className="text-center py-12">
                <div className="text-muted-foreground mb-4">
                  <Smartphone className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-card-foreground mb-2">
                  {searchTerm ? t('applicationsPricing.noDishesFoundMatchingSearch') : t('applicationsPricing.noDishesWithPricingFound')}
                </h3>
                <p className="text-muted-foreground">
                  {searchTerm ? t('applicationsPricing.tryAdjustingSearchTerms') : t('applicationsPricing.createRecipesAndSetPricing')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.id')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('pricing.dish')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('applicationsPricing.currentSellingPrice')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('applicationsPricing.applicationMarkup')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('applicationsPricing.newSellingPrice')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        {t('applicationsPricing.priceDifference')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {getFilteredAndSortedDishes().map((dish) => {
                      const currentPrice = parseFloat(dish.selling_price);
                      const percentage = getActiveApplication()?.percentage || 0;
                      const newPrice = calculateNewPrice(currentPrice, percentage);
                      const difference = newPrice - currentPrice;
                      
                      return (
                        <tr key={dish.id} className="hover:bg-muted/50">
                          <td className="px-6 py-4 whitespace-nowrap text-xs text-muted-foreground" style={{ fontSize: '0.7rem' }}>
                            #{dish.recipe_id}
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap text-left">
                            <div className="dish-item flex items-start justify-start">
                              <div className="flex-shrink-0 h-10 w-10">
                                {dish.image_url ? (
                                  <img
                                    className="h-10 w-10 rounded-lg object-cover"
                                    src={dish.image_url}
                                    alt={dish.recipe_name_en}
                                  />
                                ) : (
                                  <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                                    <ChefHat className="h-5 w-5 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              <div className="ml-3 flex-1 min-w-0">
                                {i18n.language === 'ar' ? (
                                  <>
                                    {dish.recipe_name_ar && (
                                      <div className="text-sm font-medium text-card-foreground truncate font-arabic">
                                        {dish.recipe_name_ar}
                                      </div>
                                    )}
                                    <div className="text-xs text-muted-foreground truncate">
                                      {dish.recipe_name_en || 'Unnamed Dish'}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="text-sm font-medium text-card-foreground truncate">
                                      {dish.recipe_name_en || dish.recipe_name_ar || 'Unnamed Dish'}
                                    </div>
                                    {dish.recipe_name_ar && dish.recipe_name_en && (
                                      <div className="text-xs text-muted-foreground truncate font-arabic">
                                        {dish.recipe_name_ar}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-card-foreground">
                              {formatCurrency(currentPrice)}
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-card-foreground">
                              {percentage}%
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-primary">
                              {formatCurrency(newPrice)}
                            </div>
                          </td>
                          
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`text-sm font-medium ${
                              difference > 0 ? 'text-chart-3' : 'text-muted-foreground'
                            }`}>
                              {difference > 0 ? '+' : ''}{formatCurrency(difference)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
         </div>
       )}
       
       {/* Delete Confirmation Modal */}
       <Modal isOpen={showDeleteModal && applicationToDelete} onClose={() => {
         setShowDeleteModal(false);
         setApplicationToDelete(null);
       }}>
         <div className="flex items-center gap-3 mb-6">
           <AlertCircle className="h-6 w-6 text-destructive shrink-0" />
           <h2 className="text-lg font-semibold text-card-foreground">{t('applicationsPricing.deletePricingApplication')}</h2>
         </div>
         <div className="mb-8">
           <p className="text-card-foreground">
             {t('applicationsPricing.confirmDeleteApplication', { name: applicationToDelete?.application_name })}
           </p>
         </div>
         <div className="flex flex-wrap justify-end gap-3">
           <button
             onClick={() => {
               setShowDeleteModal(false);
               setApplicationToDelete(null);
             }}
             className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
           >
             {t('applicationsPricing.cancel')}
           </button>
           <button
             onClick={confirmDeleteApplication}
             disabled={isSaving}
             className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-destructive transition-all duration-200 disabled:opacity-50"
           >
             <Trash2 className="h-4 w-4 mr-2" />
             {isSaving ? t('applicationsPricing.deleting') : t('applicationsPricing.deleteApplication')}
           </button>
         </div>
       </Modal>
     </div>
   );
 };
 
 export default ApplicationsPricing;
